import { CloudUpload, Description } from '@mui/icons-material'
import { translate } from 'locales'
import React, { useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { alertDanger, alertSuccess } from 'utils/toast'
import {
  replaceSettlementInvoice,
  uploadSettlementInvoice,
} from '../../services'
import { isValidInvoiceFileSize, isValidInvoiceFileType } from '../../utils'
import * as S from './styles'

const ACCEPTED_FILE_EXTENSIONS = '.pdf,.png,.jpg,.jpeg'

interface UploadInvoiceModalProps {
  settlementId: string
  isReplace: boolean
  onClose: () => void
  onSuccess: () => void
}

const UploadInvoiceModal = ({
  settlementId,
  isReplace,
  onClose,
  onSuccess,
}: UploadInvoiceModalProps) => {
  const [file, setFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    maxFiles: 1,
    accept: ACCEPTED_FILE_EXTENSIONS,
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        setFile(null)
        setFileError(translate('financial.invoices.errors.invalidFile'))
        return
      }

      const selected = acceptedFiles[0]
      if (!selected) return

      if (
        !isValidInvoiceFileType(selected) ||
        !isValidInvoiceFileSize(selected)
      ) {
        setFile(null)
        setFileError(translate('financial.invoices.errors.invalidFile'))
        return
      }

      setFile(selected)
      setFileError(null)
    },
  })

  const handleConfirm = async () => {
    if (!file) return

    try {
      setSaving(true)

      if (isReplace) {
        await replaceSettlementInvoice(settlementId, file)
        alertSuccess(translate('financial.invoices.replaceSuccess'))
      } else {
        await uploadSettlementInvoice(settlementId, file)
        alertSuccess(translate('financial.invoices.uploadSuccess'))
      }

      onSuccess()
    } catch (err: any) {
      const status = err?.response?.status

      if (status === 409) {
        alertDanger(translate('financial.invoices.errors.alreadyExists'))
      } else if (status === 404) {
        alertDanger(translate('financial.invoices.errors.notFound'))
      } else if (status === 502) {
        alertDanger(translate('errors.errorTry'))
      } else {
        alertDanger(
          err?.response?.data?.message || translate('errors.errorTry')
        )
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div className="overlay" />
      <div className="modal-wrapper">
        <div className="info-container">
          <S.ModalCard>
            <h2>
              {isReplace
                ? translate('financial.invoices.modal.replaceTitle')
                : translate('financial.invoices.modal.uploadTitle')}
            </h2>

            <S.Dropzone
              {...getRootProps()}
              dragActive={isDragActive}
              hasError={Boolean(fileError)}
            >
              <input {...getInputProps()} />
              {file ? (
                <span className="dropzone-file">
                  <Description />
                  <span className="dropzone-file-name">{file.name}</span>
                </span>
              ) : (
                <>
                  <CloudUpload />
                  <span className="dropzone-cta">
                    {translate('financial.invoices.modal.dropzoneCta')}
                  </span>
                  <span className="dropzone-hint">
                    {translate('financial.invoices.modal.dropzoneHint')}
                  </span>
                </>
              )}
            </S.Dropzone>

            {fileError && <S.FileError>{fileError}</S.FileError>}

            <S.ModalActions>
              <S.CancelButton type="button" onClick={onClose} disabled={saving}>
                {translate('financial.invoices.modal.cancel')}
              </S.CancelButton>

              <S.ConfirmButton
                type="button"
                onClick={handleConfirm}
                disabled={!file || saving}
              >
                {isReplace
                  ? translate('financial.invoices.replaceButton')
                  : translate('financial.invoices.uploadButton')}
              </S.ConfirmButton>
            </S.ModalActions>
          </S.ModalCard>
        </div>
      </div>
    </>
  )
}

export default UploadInvoiceModal
